import ErrorIcon from "@mui/icons-material/Error";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Alert,
  AlertTitle,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Tab,
  Tabs,
} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import isEqual from "lodash.isequal";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  fetchLatestBidGroupEmpEventsByReferenceNumber,
  fetchLatestBidLineEventsByReferenceNumber,
} from "../../api/bid";
import { fetchEmployee } from "../../api/employee";
import { getPresignedUrl, uploadFileToS3 } from "../../api/system";
import PageLoading from "../../components/PageLoading";
import BidScheduledStatusChip from "../../components/bid/BidScheduledStatusChip.jsx";
import BidTimer from "../../components/bid/BidTimer";
import CreateBidLines from "../../components/bid/CreateBidLines";
import CreateShiftBidGroup from "../../components/bid/CreateShiftBidGroup";
import ManageShiftBidChoices from "../../components/bid/ManageShiftBidChoices";
import SetUpShiftBid from "../../components/bid/SetUpShiftBid";
import ShiftBidAward from "../../components/bid/ShiftBidAward.jsx";
import ConfirmButton from "../../components/fundamentals/ConfirmButton";
import ConfirmationDialog from "../../components/fundamentals/ConfirmationDialog";
import SlideUpTransition from "../../components/fundamentals/SlideUpTransition";
import StarsBreadcrumbs from "../../components/fundamentals/StarsBreadcrumbs";
import SuccessDialog from "../../components/fundamentals/SuccessDialog";
import { selectStationFilter, selectUserSession } from "../../slices/userSession/userSessionSlice";
import { clearAwardData } from "../../util/bid/bidAward.js";
import {
  getCurrentFormScheduledStatus,
  getEmployeeFtCount,
  getEmployeePtCount,
  getS3ObjectName,
  isAwardedBid,
  isClosedBid,
  isDeletedBid,
  isOpenBid,
  isPendingBid,
  longPollingBidFetch,
} from "../../util/bid/bidCommon.js";
import { BID_AWARD_TYPE, BID_SCHEDULED_STATUS, BID_STATUS } from "../../util/bid/bidConstants";
import { buildBidS3Data } from "../../util/bid/bidEventBuilder";
import { getEligibleEmployees, getEmployeeUnrankedCount } from "../../util/bid/bidGroupUtils";
import {
  validateBidName,
  validateBidSchedDates,
  validateBidSchedEndDate,
  validateBidSchedStartDate,
  validateBidWindowDates,
  validateBidWindowTime,
} from "../../util/bid/bidInputValidation.js";
import { getUnAssignedBlockLinesCount, isAssignmentUsedToDisplay } from "../../util/bid/shiftBidUtils";
import { copyJsonObject } from "../../util/commonUtils.js";
import {
  BREADCRUMBS_TITLE,
  CANCEL_WARNING_MESSAGE_ACTION_TYPE,
  CONFIRM_TITLE,
  FEATURES,
  HOME_PATH,
  MANAGE_SHIFT_BID_TABS,
} from "../../util/constants.js";
import { padTime } from "../../util/dateTimeFormatter.js";
import {
  ADMIN_MOBILE_WARNING_MESSAGE,
  ARE_YOU_SURE_YOU_WANT_TO_CANCEL_DIALOG_MESSAGE,
  REFERENCE_NUMBER_SUB_TITLE,
} from "../../util/messageConstants";
import { getUnionContractGivenWorkgroup } from "../../util/workgroupMapper.js";
import AdminPermanentBid from "../AdminPermanentBid";

const ManageShiftBidPage = (props) => {
  const userSession = useSelector(selectUserSession);
  const stationFilter = useSelector(selectStationFilter);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isBidGroupLoading, setIsBidGroupLoading] = useState(true);
  const [isBidLinesLoading, setIsBidLinesLoading] = useState(true);
  // Generic Bid Form State
  const [schedDates, setSchedDates] = useState([null, null]);
  const [schedStartDateError, setSchedStartDateError] = useState("");
  const [schedEndDateError, setSchedEndDateError] = useState("");
  const [schedDatesErrors, setSchedDatesErrors] = useState("");
  const [windowDates, setWindowDates] = useState([null, null]);
  const [windowDatesErrors, setWindowDatesErrors] = useState("");
  const [windowDateHr, setWindowDateHr] = useState("");
  const [windowDateMin, setWindowDateMin] = useState("");
  const [windowDateTimeErrors, setWindowDateTimeErrors] = useState("");
  const [bidName, setBidName] = useState("");
  const [bidNameErrors, setBidNameErrors] = useState("");
  const [bidLines, setBidLines] = useState([]);
  const [bidGroup, setBidGroup] = useState([]);
  const [initialBidGroup, setInitialBidGroup] = useState([]);
  const [isReadyToBeScheduledByUser, setIsReadyToBeScheduledByUser] = useState(false);

  // Dialog state
  const [isExitDialogOpen, setIsExitDialogOpen] = useState(false);
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [saveErrors, setSaveErrors] = useState("");
  const [isManageShiftBidChoicesSelected, setIsManageShiftBidChoicesSelected] = useState(false);
  const [selectedBidGroupEmployeeData, setSelectedBidGroupEmployeeData] = useState("");
  const [selectedAwardedBid, setSelectedAwardedBid] = useState(null);
  const [isAwardedNameBidChoicesSelected, setIsAwardedNameBidChoicesSelected] = useState(false);
  const [isAwardedNamePermanentBidSelected, setIsAwardedNamePermanentBidSelected] = useState(false);

  const isInitialPageLoadRef = useRef(true);
  const initialBidState = useRef(null);
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [isBidGroupTabChanged, setIsBidGroupTabChanged] = useState(false);
  const [isBidLineTabChanged, setIsBidLineTabChanged] = useState(false);

  useEffect(() => {
    if (props.isDesktopView) {
      loadBidData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isDesktopView]);

  useEffect(() => {
    setWindowDatesErrors(validateBidWindowDates(windowDates, schedDates[0], props.bidData.status));
    // eslint-disable-next-line
  }, [windowDates, schedDates]);

  // set bid metadata fields into state
  useEffect(() => {
    if (props.bidData?.windowStartDate) {
      setWindowDates([props.bidData.windowStartDate, props.bidData.windowEndDate]);
      setWindowDateHr(padTime(props.bidData.windowStartDate.hour));
      setWindowDateMin(padTime(props.bidData.windowStartDate.minute));
    }
    if (props.bidData?.schedStartDate) {
      setSchedDates([props.bidData.schedStartDate, props.bidData.schedEndDate]);
    }

    if (props.bidData?.bidName) {
      setBidName(props.bidData.bidName);
    }
    setIsPageLoading(false);
  }, [props.bidData]);

  // useEffect for detecting isInitialPageLoad
  useEffect(() => {
    if (!isPageLoading && !isBidLinesLoading && !isBidGroupLoading && isInitialPageLoadRef.current) {
      initialBidState.current = getKeyDiffData();
      isInitialPageLoadRef.current = false;
    }
    // eslint-disable-next-line
  }, [isPageLoading, isBidLinesLoading, isBidGroupLoading]);

  // useEffect for detecting changes in state
  useEffect(() => {
    if (!isInitialPageLoadRef.current) {
      setIsFormChanged(true);
    }
  }, [isFormChanged, windowDates, windowDateHr, windowDateMin, schedDates, bidLines, bidGroup]);

  const getNoBidList = (bidGroup) => {
    return bidGroup
      .map((emp, idx) => {
        emp.id = idx;
        return emp;
      })
      .filter((emp) => emp.awardType === BID_AWARD_TYPE.NO_BID || emp.awardType === BID_AWARD_TYPE.NOT_ENOUGH_CHOICES);
  };

  const getIneligibleAtOpenList = (bidGroup) => {
    return bidGroup
      .map((emp, idx) => {
        emp.id = idx;
        return emp;
      })
      .filter((emp) => emp.awardType?.startsWith(BID_AWARD_TYPE.INELIGIBLE_AT_OPEN));
  };

  const handleEmployeeChoicesAsAdmin = (employeeData) => {
    setSelectedBidGroupEmployeeData(employeeData);
    setIsManageShiftBidChoicesSelected(true);
  };

  const handleAwardedNameBidChoicesClick = (awardedBid) => {
    setIsAwardedNameBidChoicesSelected(true);
    fetchEmployee(awardedBid.awardedEid)
      .then((res) => res.json())
      .then((response) => {
        if (response) {
          setSelectedBidGroupEmployeeData(response);
          setSelectedAwardedBid(awardedBid);
        } else {
          console.error("ERROR");
        }
      });
  };

  const handleAwardedNamePermanentBidClick = (awardedBid) => {
    setSelectedAwardedBid(awardedBid);
    setIsAwardedNamePermanentBidSelected(true);
  };

  const handleManagePermBidBackClick = () => {
    setSelectedBidGroupEmployeeData("");
    setSelectedAwardedBid(null);
    setIsAwardedNamePermanentBidSelected(false);
  };

  const handleManageShiftBidBackClick = () => {
    setSelectedBidGroupEmployeeData("");
    setSelectedAwardedBid(null);
    setIsAwardedNameBidChoicesSelected(false);
  };

  const loadBidData = () => {
    isInitialPageLoadRef.current = true;
    setIsFormChanged(false);
    setIsBidLinesLoading(true);
    setIsBidGroupLoading(true);
    const referenceNumber = getReferenceNumberFromHref();
    //Loading the bidGroup first. If we have success response, then loading the bidLines.
    fetchLatestBidGroupEmpEventsByReferenceNumber(userSession.eid, referenceNumber)
      .then((res) => res.json())
      .then((response) => {
        if (response.events) {
          setBidGroup(response.events);
          setInitialBidGroup(response.events);
          setIsBidGroupLoading(false);
          return fetchLatestBidLineEventsByReferenceNumber(userSession.eid, referenceNumber);
        } else {
          throw Error();
        }
      })
      .then((res) => res.json())
      .then((response) => {
        if (response.events) {
          setBidLines(
            response.events.map((event) => {
              event.id = event.lineNumber;
              return event;
            })
          );
          setIsBidLinesLoading(false);
        } else {
          throw Error();
        }
      })
      .catch((err) => {
        console.error(`Bid Data Errors: %j`, err);
        props.setLoadErrors(true);
        setBidGroup([]);
        setInitialBidGroup([]);
        setIsBidGroupLoading(false);
        setBidLines([]);
        setIsBidLinesLoading(false);
      });
  };

  const getReferenceNumberFromHref = () => {
    const uriList = window.location.href.split("/");
    return uriList[uriList.length - 1];
  };

  const prepareNewBidEventPayload = () => {
    const newBidEvent = {
      ...(props.bidData.awardLog ? { awardLog: props.bidData.awardLog } : {}),
      bidName: bidName,
      bidType: props.bidData.bidType,
      referenceNumber: props.bidData.referenceNumber,
      schedStartDate: schedDates[0],
      schedEndDate: schedDates[1],
      station: props.bidData.station,
      ...(props.bidData.stationTier ? { stationTier: props.bidData.stationTier } : {}),
      status: props.bidData.status,
      windowStartDate: windowDates[0],
      windowEndDate: windowDates[1],
      workgroup: props.bidData.workgroup,
      unionContract: getUnionContractGivenWorkgroup(props.bidData.workgroup),
      v: props.bidData.v,
    };

    if (isPendingBid(props.bidData.status)) {
      newBidEvent.scheduledStatus = getCurrentFormScheduledStatus(isReadyToBeScheduledByUser);
    }
    return newBidEvent;
  };

  const isFormValidationSuccess = () => {
    const localSchedStartDateError = validateBidSchedStartDate(schedDates);
    setSchedStartDateError(localSchedStartDateError);

    const localSchedEndDateError = validateBidSchedEndDate(schedDates);
    setSchedEndDateError(localSchedEndDateError);

    const localSchedDateErrors = validateBidSchedDates(schedDates, windowDates);
    setSchedDatesErrors(localSchedDateErrors);

    const localWindowDateErrors = validateBidWindowDates(windowDates, schedDates[0], props.bidData.status);
    setWindowDatesErrors(localWindowDateErrors);

    const localWindowDateTimeErrors = validateBidWindowTime(
      windowDates,
      windowDateHr,
      windowDateMin,
      stationFilter,
      props.bidData.status
    );
    setWindowDateTimeErrors(localWindowDateTimeErrors);

    let localBidNameErrors = "";
    // doing bid name validations only in pending state due to exiting bids prior to STARTEC-2820 having special characters
    if (isPendingBid(props.bidData.status)) {
      localBidNameErrors = validateBidName(bidName);
      setBidNameErrors(localBidNameErrors);
    }

    return (
      localSchedStartDateError === "" &&
      localSchedEndDateError === "" &&
      localSchedDateErrors === "" &&
      localWindowDateErrors === "" &&
      localWindowDateTimeErrors === "" &&
      localBidNameErrors === ""
    );
  };

  const getKeyDiffData = () => {
    let keyDiffData = {
      bidMetadata: {
        schedDates: schedDates,
        windowDates: windowDates,
        windowDateHr: windowDateHr,
        windowDateMin: windowDateMin,
        bidName: bidName,
      },
      bidLines,
      bidGroup,
    };
    keyDiffData = copyJsonObject(keyDiffData);
    return keyDiffData;
  };

  const isBidDataChanged = () => {
    if (!isInitialPageLoadRef.current) {
      const isKeyDataChanged = !isEqual(initialBidState.current, getKeyDiffData());
      let isScheduledStatusChanged = false;
      if (isPendingBid(props.bidData.status)) {
        if (props.bidData.scheduledStatus !== getCurrentFormScheduledStatus(isReadyToBeScheduledByUser))
          isScheduledStatusChanged = true;
      }
      return isKeyDataChanged || isScheduledStatusChanged;
    }
  };

  const onSaveSuccess = () => {
    setIsSaveDialogOpen(false);
    setIsSaveLoading(false);
    setIsBidLineTabChanged(false);
    setIsSuccessDialogOpen(true);
  };

  const onSaveError = () => {
    setSaveErrors("Unexpected Error");
    setIsSaveLoading(false);
    setIsSaveDialogOpen(false);
    setIsSuccessDialogOpen(true);
  };

  const handleSaveClicked = async () => {
    if (!isFormValidationSuccess()) {
      setIsSaveDialogOpen(false);
      return;
    }
    setIsSaveLoading(true);

    let isAwardDataCleared = false;
    let currentBidLines = bidLines;
    let currentBidGroup = bidGroup;
    if (isBidLineTabChanged || isBidGroupTabChanged) {
      const clearedDataResult = clearAwardData(bidLines, bidGroup);
      currentBidLines = clearedDataResult.bidLines;
      currentBidGroup = clearedDataResult.bidGroup;
      setBidLines(clearedDataResult.bidLines);
      setBidGroup(clearedDataResult.bidGroup);
      isAwardDataCleared = true;
    }

    // Save Bid Data to S3
    try {
      if (!isBidDataChanged() && !isAwardDataCleared) {
        // If no changes just show success message and exit
        onSaveSuccess();
        return;
      }

      const s3ObjectName = await getS3ObjectName(props.bidData.pk, props.bidData.station);
      const res = await getPresignedUrl("put_object", "bid-data", s3ObjectName);
      const result = await res.json();

      if (result.presignedUrl) {
        const bidS3Data = buildBidS3Data(
          userSession,
          prepareNewBidEventPayload(),
          currentBidLines,
          currentBidGroup,
          s3ObjectName
        );
        const response = await uploadFileToS3(result.presignedUrl, bidS3Data);

        if (response.status === 200) {
          const requestId = response.headers.get("x-amz-request-id");
          props.currentRequestId.current = requestId;
          await longPollingBidFetch(props.bidData.referenceNumber, userSession, requestId);
          onSaveSuccess();
        } else {
          const errorMessage = "Bad response from uploadFileToS3";
          console.error(errorMessage);
          throw Error(errorMessage);
        }
      } else {
        const errorMessage = "Bad response from getPresignedUrl";
        console.error(errorMessage);
        throw Error(errorMessage);
      }
    } catch (err) {
      console.error("Error during handleSaveClicked: ", err);
      onSaveError();
    }
  };

  const handleClickSubmitDialogOpen = () => {
    if (!isFormValidationSuccess()) {
      setIsSaveDialogOpen(false);
      return;
    }
    setIsReadyToBeScheduledByUser(false);
    setIsSaveDialogOpen(true);
  };

  const handleClickSaveDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsSaveDialogOpen(false);
  };

  const handleClickCancelDialogOpen = () => {
    setIsExitDialogOpen(true);
  };

  const handleClickCancelDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    setIsExitDialogOpen(false);
  };

  const handleSchedDateChange = (event) => {
    setSchedStartDateError(validateBidSchedStartDate(event));
    setSchedEndDateError(validateBidSchedEndDate(event));
    setSchedDatesErrors(validateBidSchedDates(event, windowDates));
    setSchedDates(event);
  };

  const handleWindowDateChange = (event) => {
    setWindowDatesErrors(validateBidWindowDates(event, schedDates[0], props.bidData.status));
    if (event[0] && !event[1]) {
      event[1] = event[0].plus({ days: 7 });
    }
    let newWindowStartDate = event[0];
    let newWindowEndDate = event[1];
    if (windowDates[0] && windowDates[1] && windowDateHr && windowDateMin) {
      newWindowStartDate = newWindowStartDate?.set({ hour: windowDateHr, minute: windowDateMin });
      newWindowEndDate = newWindowEndDate?.set({ hour: windowDateHr, minute: windowDateMin });
    }
    setWindowDates([newWindowStartDate, newWindowEndDate]);
  };

  const handleBidWindowHourChange = (event) => {
    setWindowDateTimeErrors(
      validateBidWindowTime(windowDates, event.target.value, windowDateMin, stationFilter, props.bidData.status)
    );
    let newWindowStartDate = windowDates[0];
    let newWindowEndDate = windowDates[1];
    newWindowStartDate = newWindowStartDate.set({ hour: parseInt(event.target.value) });
    newWindowEndDate = newWindowEndDate.set({ hour: parseInt(event.target.value) });
    setWindowDates([newWindowStartDate, newWindowEndDate]);
    setWindowDateHr(event.target.value);
  };

  const handleBidWindowMinuteChange = (event) => {
    setWindowDateTimeErrors(
      validateBidWindowTime(windowDates, windowDateHr, event.target.value, stationFilter, props.bidData.status)
    );
    let newWindowStartDate = windowDates[0];
    let newWindowEndDate = windowDates[1];
    newWindowStartDate = newWindowStartDate.set({ minute: parseInt(event.target.value) });
    newWindowEndDate = newWindowEndDate.set({ minute: parseInt(event.target.value) });
    setWindowDates([newWindowStartDate, newWindowEndDate]);
    setWindowDateMin(event.target.value);
  };

  const handleBidNameChange = (event) => {
    setBidNameErrors(validateBidName(event.target.value));
    setBidName(event.target.value);
  };

  const handleBidLineChange = (event) => {
    setBidLines(event);
    setIsBidLineTabChanged(true);
  };

  const handleBidGroupChange = (event) => {
    setBidGroup(event);
    setIsBidGroupTabChanged(true);
  };

  const isBidWindowLessThanSevenDays = () => {
    return windowDates[0] && windowDates[1] && windowDates[1].diff(windowDates[0], ["days"]).values.days < 7;
  };

  const isSetUpShiftBidTabInputValid = () => {
    return (
      schedStartDateError === "" &&
      schedEndDateError === "" &&
      schedDatesErrors === "" &&
      windowDatesErrors === "" &&
      windowDateTimeErrors === "" &&
      bidNameErrors === ""
    );
  };

  const getSchedulableValidationErrors = () => {
    let validationErrors = [];

    if (!schedDates[0] || !schedDates[1]) {
      validationErrors.push("Bid Effective Dates are missing");
    }

    if (!windowDates[0] || !windowDates[1] || !windowDateHr || !windowDateMin) {
      validationErrors.push("Bid Window Dates are missing");
    }

    const unrankedEmployeeCount = getEmployeeUnrankedCount(getEligibleEmployees(bidGroup));
    if (unrankedEmployeeCount > 0) {
      if (unrankedEmployeeCount === 1) {
        validationErrors.push(`${unrankedEmployeeCount} Employee is unranked`);
      } else {
        validationErrors.push(`${unrankedEmployeeCount} Employees are unranked`);
      }
    }

    if (getEligibleEmployees(bidGroup).length === 0 && bidLines.length === 0) {
      validationErrors.push("Bid Lines and Eligible Employees are missing");
    }

    if (getEmployeeFtCount(getEligibleEmployees(bidGroup)) > getEmployeeFtCount(bidLines)) {
      validationErrors.push("Not enough FT Bid Lines");
    }

    if (getEmployeeFtCount(getEligibleEmployees(bidGroup)) < getEmployeeFtCount(bidLines)) {
      validationErrors.push("Too many FT Bid Lines");
    }

    if (getEmployeePtCount(getEligibleEmployees(bidGroup)) > getEmployeePtCount(bidLines)) {
      validationErrors.push("Not enough PT Bid Lines");
    }

    if (getEmployeePtCount(getEligibleEmployees(bidGroup)) < getEmployeePtCount(bidLines)) {
      validationErrors.push("Too many PT Bid Lines");
    }
    return validationErrors;
  };

  const renderScheduledSection = () => {
    const validationErrors = getSchedulableValidationErrors();

    return (
      <>
        {validationErrors.length > 0 && (
          <>
            <Typography sx={{ marginTop: "30px" }}>
              Please fix the following before the bid can be scheduled:
            </Typography>
            {validationErrors.map((validationError) => {
              return (
                <Typography key={validationError} sx={{ display: "flex", alignItems: "center", margin: "10px" }}>
                  <WarningIcon sx={{ marginRight: "5px", color: "primary.swaYellow" }} />
                  {validationError}
                </Typography>
              );
            })}
          </>
        )}
        <FormControlLabel
          label="Bid is ready to be scheduled"
          control={
            <Checkbox
              checked={isReadyToBeScheduledByUser}
              onChange={() => setIsReadyToBeScheduledByUser(!isReadyToBeScheduledByUser)}
              disabled={validationErrors.length > 0 || isSaveLoading}
            />
          }
          sx={{ marginTop: "10px", marginLeft: "1px" }}
        ></FormControlLabel>
      </>
    );
  };

  const getHardStopValidationErrors = () => {
    let validationErrors = [];

    if (isOpenBid(props.bidData.status) || isClosedBid(props.bidData.status)) {
      const unrankedEmployeeCount = getEmployeeUnrankedCount(getEligibleEmployees(bidGroup));
      if (unrankedEmployeeCount > 0) {
        if (unrankedEmployeeCount === 1) {
          validationErrors.push(`${unrankedEmployeeCount} Employee is unranked`);
        } else {
          validationErrors.push(`${unrankedEmployeeCount} Employees are unranked`);
        }
      }
      const unAssignedBlockLinesCount = getUnAssignedBlockLinesCount(bidLines);
      if (unAssignedBlockLinesCount > 0) {
        if (unAssignedBlockLinesCount === 1) {
          validationErrors.push(`${unAssignedBlockLinesCount} Block Line is unassigned`);
        } else {
          validationErrors.push(`${unAssignedBlockLinesCount} Block Lines are unassigned`);
        }
      }
    }

    return validationErrors;
  };

  const isAwardTabAvailable = () => {
    return props.bidData.status === BID_STATUS.CLOSED || props.bidData.status === BID_STATUS.AWARDED;
  };

  const renderHardStopErrorSection = () => {
    const validationErrors = getHardStopValidationErrors();

    if (validationErrors.length > 0) {
      return validationErrors.map((validationError) => {
        return (
          <Typography key={validationError} sx={{ display: "flex", alignItems: "center", margin: "10px" }}>
            <ErrorIcon sx={{ marginRight: "5px", color: "primary.swaRed" }} />
            {validationError}
          </Typography>
        );
      });
    }
  };

  const renderSaveShiftBidDialog = () => {
    return (
      <Dialog open={isSaveDialogOpen} TransitionComponent={SlideUpTransition} keepMounted fullWidth>
        <DialogTitle>Save changes</DialogTitle>
        <DialogContent>
          <DialogContentText component={"div"}>
            <Grid container>
              <Grid item xs={12}>
                <Typography sx={{ color: "black", marginBottom: "10px" }}>
                  You are saving changes made to this Shift Bid.
                  {isSaveLoading ? (
                    <Typography display={"inline"}>
                      {" "}
                      This may take a few minutes. Your patience is appreciated.{" "}
                      <Typography display={"inline"} sx={{ color: "black" }}>
                        🙂
                      </Typography>
                    </Typography>
                  ) : (
                    ""
                  )}
                </Typography>
              </Grid>
            </Grid>
          </DialogContentText>

          {isPendingBid(props.bidData.status) && (
            <>
              {isBidWindowLessThanSevenDays() ? (
                <Alert severity="warning" sx={{ marginBottom: "30px" }}>
                  <AlertTitle>
                    <strong>WARNING</strong>
                  </AlertTitle>
                  You have entered a Bid Window with a duration less than 7 days.
                </Alert>
              ) : (
                ""
              )}
              {renderScheduledSection()}
            </>
          )}
          {(isOpenBid(props.bidData.status) || isClosedBid(props.bidData.status)) && renderHardStopErrorSection()}
        </DialogContent>
        <DialogActions>
          <ConfirmButton
            variant="outlined"
            color="primary"
            onClick={handleClickSaveDialogClose}
            disabled={isSaveLoading}
          >
            Cancel
          </ConfirmButton>
          <ConfirmButton
            variant="contained"
            color="primary"
            onClick={handleSaveClicked}
            isLoading={isSaveLoading}
            disabled={
              (isOpenBid(props.bidData.status) || isClosedBid(props.bidData.status)) &&
              getHardStopValidationErrors().length > 0
            }
          >
            Save
          </ConfirmButton>
        </DialogActions>
      </Dialog>
    );
  };

  const getBidAdminPath = () => {
    return props.featureConfig.childItems.find((childItem) => childItem.key === FEATURES.BID_ADMINISTRATION).path;
  };

  const breadcrumbsList = [
    BREADCRUMBS_TITLE.HOME,
    BREADCRUMBS_TITLE.BID_ADMINISTRATION,
    BREADCRUMBS_TITLE.MANAGE_SHIFT_BID,
  ];
  const breadcrumbsLinks = [HOME_PATH, getBidAdminPath()];
  const renderDesktopManageShiftBidPage = () => {
    if (isPageLoading) {
      return <PageLoading />;
    }
    return (
      <>
        {isManageShiftBidChoicesSelected ? (
          <ManageShiftBidChoices
            selectedBidGroupEmployeeData={selectedBidGroupEmployeeData}
            referenceNumber={getReferenceNumberFromHref()}
            featureConfig={props.featureConfig}
            setIsManageShiftBidChoicesSelected={setIsManageShiftBidChoicesSelected}
            station={props.bidData.station}
            isDesktopView={props.isDesktopView}
            isAwardedEmpReadOnlyView={false}
            bidData={props.bidData}
            setIsBidGroupTabChanged={setIsBidGroupTabChanged}
            useAssignmentForEmp={isAssignmentUsedToDisplay(props.bidData)}
          />
        ) : isAwardedNameBidChoicesSelected ? (
          <ManageShiftBidChoices
            selectedBidGroupEmployeeData={selectedBidGroupEmployeeData}
            referenceNumber={getReferenceNumberFromHref()}
            featureConfig={props.featureConfig}
            handleManageShiftBidBackClick={handleManageShiftBidBackClick}
            station={props.bidData.station}
            isDesktopView={props.isDesktopView}
            isAwardedEmpReadOnlyView={true}
            selectedAwardedBid={selectedAwardedBid}
            bidData={props.bidData}
            useAssignmentForEmp={isAssignmentUsedToDisplay(props.bidData)}
          />
        ) : isAwardedNamePermanentBidSelected ? (
          <AdminPermanentBid
            isAwardedEmpReadOnlyView={true}
            bidData={props.bidData}
            handleManagePermBidBackClick={handleManagePermBidBackClick}
            selectedAwardedBid={selectedAwardedBid}
            featureConfig={props.featureConfig}
            isDesktopView={props.isDesktopView}
          />
        ) : (
          <>
            <StarsBreadcrumbs
              breadcrumbsList={breadcrumbsList}
              breadcrumbsLinks={breadcrumbsLinks}
              isConfirmationRequired={isFormChanged}
              cancelActionType={CANCEL_WARNING_MESSAGE_ACTION_TYPE.EDITS}
            />
            {/* Title & Cancel/Submit Buttons */}
            <Grid container>
              <Grid
                item
                xs={6}
                sx={{ display: "flex", justifyContent: "start", paddingTop: "10px", paddingLeft: "2rem" }}
              >
                <Typography variant="h5" component="span">
                  Manage Shift Bid
                </Typography>
              </Grid>
              <Grid item xs={6} sx={{ display: "flex", justifyContent: "end", paddingRight: "2rem" }}>
                <Box sx={{ paddingRight: "10px", paddingTop: "10px" }}>
                  <Button
                    sx={{ width: "100px" }}
                    variant="outlined"
                    color="primary"
                    onClick={handleClickCancelDialogOpen}
                  >
                    Cancel
                  </Button>
                </Box>

                <Box sx={{ paddingLeft: "10px", paddingTop: "10px" }}>
                  <Button
                    sx={{ width: "100px" }}
                    variant="contained"
                    color="primary"
                    onClick={handleClickSubmitDialogOpen}
                    disabled={
                      isDeletedBid(props.bidData.status) ||
                      isBidLinesLoading ||
                      isBidGroupLoading ||
                      props.loadErrors ||
                      (!isFormChanged && isAwardedBid(props.bidData.status))
                    }
                  >
                    Save
                  </Button>
                </Box>
              </Grid>
            </Grid>
            <Box sx={{ textAlign: "left", marginLeft: "32px" }}>
              <Typography variant="body2" component="span" sx={{ display: "flex", alignItems: "center" }}>
                <Typography variant="body2" component="span" sx={{ display: "inline-block" }}>
                  Status:
                </Typography>
                <Typography
                  variant="body2"
                  component="span"
                  sx={{ fontWeight: "bold", display: "inline-block", marginLeft: "5px" }}
                >
                  {props.bidData.status}
                </Typography>
                <Box sx={{ display: "inline-block", marginLeft: "5px" }}>
                  <BidScheduledStatusChip scheduledStatus={props.bidData.scheduledStatus} />
                </Box>
                {props.bidData.status !== BID_STATUS.DELETED &&
                  !(
                    props.bidData.status === BID_STATUS.PENDING &&
                    props.bidData.scheduledStatus === BID_SCHEDULED_STATUS.DELAYED
                  ) &&
                  windowDates[0] !== null &&
                  windowDates[1] !== null &&
                  windowDateHr &&
                  windowDateMin && (
                    <>
                      <Typography variant="body2" component="span" sx={{ marginLeft: "5px", marginRight: "5px" }}>
                        |
                      </Typography>
                      <Typography
                        variant="body2"
                        component="span"
                        sx={{ color: "primary.swaRed", fontWeight: "bold", display: "inline-block" }}
                      >
                        <BidTimer windowDates={windowDates} station={props.bidData.station} />
                      </Typography>
                    </>
                  )}
              </Typography>
            </Box>
            {/* The TABS! */}
            <Box sx={{ width: "100%", margin: "auto", paddingLeft: "2rem", paddingRight: "2rem", marginTop: "1rem" }}>
              <Tabs
                value={props.selectedTab}
                onChange={props.handleChangeTab}
                sx={{ width: "inherit", margin: "auto", bgcolor: "secondary.swaGray1" }}
                variant="fullWidth"
              >
                <Tab
                  sx={{ fontWeight: "bold", height: "70px" }}
                  label={MANAGE_SHIFT_BID_TABS.SET_UP_THE_SHIFT_BID}
                  icon={
                    !isSetUpShiftBidTabInputValid() ? <ErrorOutlineIcon sx={{ color: "primary.swaRed" }} /> : undefined
                  }
                  iconPosition="start"
                ></Tab>
                <Tab sx={{ fontWeight: "bold" }} label={MANAGE_SHIFT_BID_TABS.MANAGE_BID_LINES}></Tab>
                <Tab sx={{ fontWeight: "bold" }} label={MANAGE_SHIFT_BID_TABS.MANAGE_BID_GROUP}></Tab>
                {isAwardTabAvailable() && <Tab sx={{ fontWeight: "bold" }} label={MANAGE_SHIFT_BID_TABS.AWARD}></Tab>}
              </Tabs>

              {props.selectedTab === 0 && (
                <div>
                  <SetUpShiftBid
                    bid={props.bidData}
                    schedDates={schedDates}
                    setSchedDates={handleSchedDateChange}
                    schedStartDateError={schedStartDateError}
                    schedEndDateError={schedEndDateError}
                    schedDatesErrors={schedDatesErrors}
                    windowDates={windowDates}
                    setWindowDates={handleWindowDateChange}
                    windowDatesErrors={windowDatesErrors}
                    windowDateHr={windowDateHr}
                    setWindowDateHr={handleBidWindowHourChange}
                    windowDateMin={windowDateMin}
                    setWindowDateMin={handleBidWindowMinuteChange}
                    windowDateTimeErrors={windowDateTimeErrors}
                    bidName={bidName}
                    bidNameErrors={bidNameErrors}
                    setBidNameChange={handleBidNameChange}
                  />
                </div>
              )}

              {props.selectedTab === 1 && (
                <div>
                  <CreateBidLines
                    bid={props.bidData}
                    bidGroup={bidGroup}
                    bidLines={bidLines}
                    isBidLinesLoading={isBidLinesLoading}
                    setBidLines={handleBidLineChange}
                    schedDates={schedDates}
                    bidName={bidName}
                  />
                </div>
              )}

              {props.selectedTab === 2 && (
                <div>
                  <CreateShiftBidGroup
                    bid={props.bidData}
                    bidLines={bidLines}
                    bidGroup={bidGroup}
                    isBidGroupLoading={isBidGroupLoading}
                    setIsBidGroupLoading={setIsBidGroupLoading}
                    setBidGroup={handleBidGroupChange}
                    setBidLines={setBidLines}
                    schedDates={schedDates}
                    windowDates={windowDates}
                    bidName={bidName}
                    handleEmployeeChoicesAsAdmin={handleEmployeeChoicesAsAdmin}
                    initialBidGroup={initialBidGroup}
                    userSession={userSession}
                  />
                </div>
              )}

              {isAwardTabAvailable() && props.selectedTab === 3 && (
                <div>
                  <ShiftBidAward
                    bid={props.bidData}
                    setBid={props.setBidData}
                    bidLines={bidLines}
                    isBidLinesLoading={isBidLinesLoading}
                    setBidLines={setBidLines}
                    isBidLineTabChanged={isBidLineTabChanged}
                    bidGroup={bidGroup}
                    isBidGroupLoading={isBidGroupLoading}
                    setBidGroup={setBidGroup}
                    isBidGroupTabChanged={isBidGroupTabChanged}
                    schedDates={schedDates}
                    bidName={bidName}
                    noBidList={getNoBidList(bidGroup)}
                    ineligibleAtOpenList={getIneligibleAtOpenList(bidGroup)}
                    bidAdminPath={getBidAdminPath()}
                    isFormChanged={isFormChanged}
                    loadBidData={props.reloadBidData}
                    featureConfig={props.featureConfig}
                    handleAwardedNameBidChoicesClick={handleAwardedNameBidChoicesClick}
                    handleAwardedNamePermanentBidClick={handleAwardedNamePermanentBidClick}
                    currentRequestId={props.currentRequestId}
                  />
                </div>
              )}
            </Box>

            <ConfirmationDialog
              isDialogOpen={isExitDialogOpen}
              title={CONFIRM_TITLE}
              message={ARE_YOU_SURE_YOU_WANT_TO_CANCEL_DIALOG_MESSAGE(CANCEL_WARNING_MESSAGE_ACTION_TYPE.EDITS)}
              handleNoButtonClicked={handleClickCancelDialogClose}
              redirectTo={getBidAdminPath()}
            />
            {isSaveDialogOpen && renderSaveShiftBidDialog()}
            <SuccessDialog
              isSuccessDialogOpen={isSuccessDialogOpen}
              title={"Bid Confirmation"}
              subTitle={REFERENCE_NUMBER_SUB_TITLE(props.bidData.referenceNumber)}
              message={"The bid has been saved!"}
              hasError={saveErrors !== ""}
              error={saveErrors}
              handleOKClick={() => {
                setSaveErrors("");
                setIsSuccessDialogOpen(false);
                props.reloadBidData();
              }}
            />
          </>
        )}
      </>
    );
  };

  const renderMobileManageShiftBidPage = () => {
    return <div className="page-error">{ADMIN_MOBILE_WARNING_MESSAGE}</div>;
  };

  return (
    <div className="App-contents enable-scroll" data-testid="manage-shift-bid-page">
      {props.isDesktopView ? renderDesktopManageShiftBidPage() : renderMobileManageShiftBidPage()}
    </div>
  );
};

ManageShiftBidPage.propTypes = {
  featureConfig: PropTypes.object.isRequired,
  isDesktopView: PropTypes.bool.isRequired,
  bidData: PropTypes.object.isRequired,
  setBidData: PropTypes.func.isRequired,
  reloadBidData: PropTypes.func.isRequired,
  selectedTab: PropTypes.number.isRequired,
  handleChangeTab: PropTypes.func.isRequired,
  loadErrors: PropTypes.bool,
  setLoadErrors: PropTypes.func.isRequired,
  currentRequestId: PropTypes.object.isRequired,
};

export default ManageShiftBidPage;
